<template>
	<main>
		<TopBottomLayout v-if="hotrankingCategoryData" class="c_not-center">
			<div v-for="(item, index) in hotrankingCategoryData" :key="index" class="ly_col-48 ly_wrapper">
				<div v-for="(key, keyIndex) in Object.keys(item)" :key="keyIndex" class="main_title ly_col-24 c_responsive">
					<h4>{{ key }}</h4>
					<HotRankSlideList :data="(item as any)[key]" />
				</div>
				<div v-if="hotrankingTabData" class="ly_col-48 ly_list-wrapper">
					<div
						v-if="displayStore.isDesktop && hotrankingTabData.hot_ranking_banner.hot_ranking_banner_image_pc_path"
						class="banner_hot-ranking c_responsive"
					>
						<img
							:src="hotrankingTabData?.hot_ranking_banner.hot_ranking_banner_image_pc_path"
							@click="clickBanner(hotrankingTabData?.hot_ranking_banner.hot_ranking_url)"
						/>
					</div>
					<div
						v-if="!displayStore.isDesktop && hotrankingTabData.hot_ranking_banner.hot_ranking_banner_image_mo_path"
						class="banner_hot-ranking c_responsive"
					>
						<img
							:src="hotrankingTabData?.hot_ranking_banner.hot_ranking_banner_image_mo_path"
							@click="clickBanner(hotrankingTabData?.hot_ranking_banner.hot_ranking_url)"
						/>
					</div>
					<MarketTimingLayout class="ly_list-wrapper">
						<StickyTabBar
							v-if="Object.keys(hotrankingTabData?.hot_ranking_tab).length > 0"
							:items="Object.keys(hotrankingTabData?.hot_ranking_tab)"
						></StickyTabBar>
						<ListLayout v-if="expandedHotRankingMenuDepth1.includes(currentHotRankingName)" class="c_responsive">
							<ConnectedSquareTab
								:selected-item="selectedHotRankingDepth3"
								:items="expandedHotRankingMenuDepth3"
								@update:detail-tab="onChangeStockSize"
							/>
						</ListLayout>
						<ListLayout
							v-if="hotRankingData"
							:class="['c_responsive', hotRankingData?.hot_ranking?.length <= 0 && 'ly_list']"
						>
							<template v-if="hotRankingData.hot_ranking && hotRankingData.hot_ranking.length > 0">
								<BookmarkList :items="hotRankingData.hot_ranking" @update:book-mark-refetch="handleBookmarkUpdate" />
								<MoreButton
									v-if="
										(selectedTab === '차트영웅 Ⅰ' || selectedTab === '차트영웅 ⅠⅠ' || selectedTab === '차트영웅 ⅠⅠⅠ') &&
										hasNextPage
									"
									@click="onChangeMore"
								/>
							</template>
							<p v-else class="not_content">등록된 데이터가 없습니다.</p>
						</ListLayout>
					</MarketTimingLayout>
				</div>
			</div>
		</TopBottomLayout>
	</main>
</template>

<script setup lang="ts">
// core
import { computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';

// api
import {
	HotRankingItem,
	useGetHotrankingCategoryApi_KR,
	useGetHotrankingTabApi_KR,
	useInfiniteHotrankingApi_KR,
} from '@/api/hotRanking';
import MoreButton from '@/components/atom/button/MoreButton.vue';
import ConnectedSquareTab from '@/components/atom/tabBar/ConnectedSquareTab.vue';
// component
import StickyTabBar from '@/components/atom/tabBar/StickyTabBar.vue';
import ListLayout from '@/components/layout/main/ListLayout.vue';
import MarketTimingLayout from '@/components/layout/main/MarketTimingLayout.vue';
import TopBottomLayout from '@/components/layout/main/TopBottomLayout.vue';
import BookmarkList from '@/components/rankList/BookmarkList.vue';
import HotRankSlideList from '@/components/rankList/HotRankSlideList.vue';
// store
import { useDisplayStore } from '@/stores/display';

// -----variable-----
const route = useRoute();
const router = useRouter();
const displayStore = useDisplayStore();
const expandedHotRankingMenuDepth1 = ['매출액 급성장', '이익 급성장']; // 두 탭의 경우만 3 뎁스까지 메뉴 존재
const expandedHotRankingMenuDepth3 = ['대형주', '중형주', '소형주'];

const queryClient = useQueryClient();

const pk = computed(() => route.params.pk ?? '');
const categoryPk = computed(() => route.params.categoryPk ?? '');
const selectedTab = computed(() => {
	return route.query.tab ?? (hotrankingTabData?.value && Object.keys(hotrankingTabData?.value.hot_ranking_tab)[0]);
});

const currentHotRankingName = computed(() => {
	if (!hotrankingCategoryData.value?.hotranking_category || !pk.value) return '';

	// hotranking_category는 객체이므로 모든 키의 배열을 순회해서 찾아야 함
	const allCategories = Object.values(hotrankingCategoryData.value.hotranking_category).flat();
	const currentItem = allCategories.find((item) => item.hot_ranking_pk === Number(pk.value));

	return currentItem?.hot_ranking_name ?? '';
});

const selectedHotRankingDepth3 = computed(() =>
	expandedHotRankingMenuDepth1.includes(currentHotRankingName.value)
		? route.query.sort2
			? String(route.query.sort2)
			: '대형주'
		: ''
);
// -----api-----
const { data: hotrankingCategoryData } = useGetHotrankingCategoryApi_KR(
	computed(() => ({ hot_ranking_category_pk: String(categoryPk.value) }))
);
const { data: hotrankingTabData } = useGetHotrankingTabApi_KR(
	computed(() => ({ hot_ranking_pk: String(pk.value) })),
	computed(() => !!pk.value)
);
const convertStockTypeKorToCode = (kor: string): string => {
	const stockTypeMap: Record<string, string> = {
		대형주: 'MTL',
		중형주: 'STM',
		소형주: 'SMA',
	};

	return stockTypeMap[kor] || kor;
};
const {
	data: hotRankingData,
	fetchNextPage,
	hasNextPage,
} = useInfiniteHotrankingApi_KR(
	computed(() => ({
		hot_ranking_pk: String(pk.value),
		division_top: selectedTab.value ? String(selectedTab.value) : '',
		division_mid: convertStockTypeKorToCode(selectedHotRankingDepth3.value) ?? '',
	})),
	computed(() => !!hotrankingTabData.value)
);

const handleBookmarkUpdate = ({ event_pk, bookmark }: { event_pk: string; bookmark: number }) => {
	// 방법 2 적용 - 캐시 직접 업데이트
	const url = '/v1/front/getHotranking';

	queryClient.setQueryData(
		[
			url,
			computed(() => ({
				hot_ranking_pk: String(pk.value),
				division_top: selectedTab.value ? String(selectedTab.value) : '',
				division_mid: convertStockTypeKorToCode(selectedHotRankingDepth3.value) ?? '',
			})),
		],
		(oldData: any) => {
			if (!oldData) return oldData;

			const newPages = oldData.pages.map((page: any) => ({
				...page,
				data: {
					...page.data,
					hot_ranking: page.data.hot_ranking.map((item: HotRankingItem) =>
						Number(item.event_pk) === Number(event_pk) ? { ...item, bookmark } : item
					),
				},
			}));

			return { ...oldData, pages: newPages };
		}
	);
};

// -----function-----
// 핫랭킹 메뉴 Depth3 탭 클릭 시 실행 함수
const onChangeStockSize = (tab: string) => {
	router.replace({ query: { ...route.query, sort2: tab } });
};

const clickBanner = (url: string) => {
	let agent = navigator.userAgent.toLowerCase();
	if (agent.indexOf('android') !== -1 && window.masterpick) {
		window.location.href = url;
	} else if (
		(agent.indexOf('iphone') !== -1 || agent.indexOf('ipad') !== -1 || agent.indexOf('macintosh') !== -1) &&
		window.webkit &&
		window.webkit.messageHandlers &&
		window.webkit.messageHandlers.nativeUrlOpenBrowser
	) {
		window.location.href = url;
	} else {
		window.open(url, '_self');
	}
};

const onChangeMore = () => {
	fetchNextPage();
};

const resetInfiniteHotrankingData = () => {
	queryClient.invalidateQueries({ queryKey: ['/v1/front/getHotranking'] });
};

// -----composition-----
watch(
	() => route.query.sort1,
	() => {
		if (expandedHotRankingMenuDepth1.includes(currentHotRankingName.value)) {
			router.replace({ query: { ...route.query, sort2: '대형주' } });
		}
	}
);

onMounted(() => {
	resetInfiniteHotrankingData();
});
</script>

<style lang="scss" scoped>
.banner_hot-ranking {
	height: 18rem;
	border-radius: 0.8rem;
	/* overflow: hidden; */
	cursor: pointer;
	@media screen and (max-width: 1280px) {
		height: 12rem;
	}
	img {
		height: 100%;
		object-fit: fill;
		object-position: center;
		border-radius: 0.8rem;
	}
}
.not_content {
	font-size: var(--font-s-mid);
	font-weight: var(--font-w-semi-bold);
	line-height: normal;
	text-align: center;
}
main {
	display: flex;
	/* height: 100%; */
}
.c_not-center {
	justify-content: unset;
	display: flex;
	flex: 1;
}
.ly_wrapper {
	height: 100%;
}
.ly_list-wrapper {
	height: 100%;
}
.ly_list {
	height: 100%;
	justify-content: center;
}
</style>
